import { useState } from 'react'
import { Download } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { MemberCreditNotesPanel } from '@/components/member/member-credit-notes-panel'
import { MemberInvoicesPanel } from '@/components/member/member-invoices-panel'
import { MemberPaymentsPanel } from '@/components/member/member-payments-panel'
import { useMemberBalance, useMemberInvoices } from '@/hooks/use-billing'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { getApiErrorMessage } from '@/lib/api'
import { downloadMemberStatement } from '@/lib/billing'
import { cn, formatKES } from '@/lib/utils'

type BillsSubTab = 'invoices' | 'credit-notes' | 'payments'
const SUB_TABS: { id: BillsSubTab; label: string }[] = [
  { id: 'invoices', label: 'Invoices' },
  { id: 'credit-notes', label: 'Credit' },
  { id: 'payments', label: 'Payments' },
]
const VALID_SUB_TABS: BillsSubTab[] = ['invoices', 'credit-notes', 'payments']

export function MemberBills() {
  const [subTab, setSubTab] = useTabQueryParam(VALID_SUB_TABS, 'invoices')
  const { data: balanceData } = useMemberBalance()
  const { data: billsData } = useMemberInvoices({ excludeInvoiceType: 'credit_note' })
  const [downloadingStatement, setDownloadingStatement] = useState(false)
  const [statementError, setStatementError] = useState<string | null>(null)

  // Positive = owes, negative = in credit
  const balance = balanceData?.data.balance ?? null
  const unpaidCount = (billsData?.data ?? []).filter(
    (inv) => inv.status === 'sent' || inv.status === 'overdue',
  ).length

  async function handleDownloadStatement() {
    setStatementError(null)
    setDownloadingStatement(true)
    try {
      await downloadMemberStatement()
    } catch (err) {
      setStatementError(getApiErrorMessage(err, 'Failed to download statement'))
    } finally {
      setDownloadingStatement(false)
    }
  }

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title="Bills & Payments"
        description="Your bills, credit, and payment history."
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full rounded-card border border-border-2 bg-card px-5 py-4 sm:w-auto sm:min-w-[240px]">
          <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Your balance
          </p>
          <p
            className={cn(
              'mt-2 font-serif text-stat-sm leading-none tracking-tight',
              balance != null && balance > 0 ? 'text-status-overdue-text' : 'text-status-paid-text',
            )}
          >
            {balance == null
              ? '…'
              : balance > 0
                ? `${formatKES(balance)} due`
                : balance < 0
                  ? `${formatKES(-balance)} credit`
                  : formatKES(0)}
          </p>
          <p className="mt-1.5 font-sans text-xs text-muted-foreground">
            {balance == null
              ? ''
              : balance > 0
                ? `${unpaidCount} unpaid bill${unpaidCount === 1 ? '' : 's'}`
                : balance < 0
                  ? 'Will be taken off your next bill'
                  : 'All bills paid'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            loading={downloadingStatement}
            onClick={() => void handleDownloadStatement()}
          >
            <Download size={14} className="mr-1.5" />
            Download statement
          </Button>
        </div>
      </div>
      {statementError && (
        <p className="font-sans text-xs text-destructive" role="alert">
          {statementError}
        </p>
      )}

      <div className="flex gap-1 border-b border-border-2">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              subTab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'invoices' && <MemberInvoicesPanel />}
      {subTab === 'credit-notes' && <MemberCreditNotesPanel />}
      {subTab === 'payments' && <MemberPaymentsPanel />}
    </PageShell>
  )
}
